import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { warehouse: "Warenhaus", shop: "Shop", vending_machine: "Automat" };
const EMPTY = { code: "", name: "", type: "warehouse", address: "" };

export default function Lagerorte() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const qc = useQueryClient();
  const nav = useNavigate();

  const locations = useQuery({
    queryKey: ["locations-full"],
    queryFn: async () => (await api.get("/locations")).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["locations-full"] });
    qc.invalidateQueries({ queryKey: ["locations"] });
    qc.invalidateQueries({ queryKey: ["inv-by-product"] });
  };

  const save = useMutation({
    mutationFn: async () => {
      if (editingId) {
        // code is immutable via UI to preserve historical joins
        const { code: _c, ...rest } = form;
        return (await api.put(`/locations/${editingId}`, rest)).data;
      }
      return (await api.post("/locations", form)).data;
    },
    onSuccess: () => {
      toast.success(editingId ? "Lagerort aktualisiert" : "Lagerort angelegt");
      setOpen(false); setForm(EMPTY); setEditingId(null);
      invalidate();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/locations/${id}`)).data,
    onSuccess: (data) => {
      toast.success(data?.archived ? "Lagerort archiviert (Historie erhalten)" : "Lagerort gelöscht");
      invalidate();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const openNew = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (l) => {
    setEditingId(l.id);
    setForm({ code: l.code, name: l.name, type: l.type, address: l.address || "" });
    setOpen(true);
  };
  const confirmDelete = (l) => {
    const msg = `Lagerort "${l.name}" löschen?\n\n` +
      `Bei aktuellem Bestand > 0 wird die Aktion blockiert.\n` +
      `Bei bereits vorhandenen Buchungen wird archiviert (Historie bleibt erhalten).`;
    if (window.confirm(msg)) del.mutate(l.id);
  };

  return (
    <div data-testid="locations-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6 gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-cyan-400">Netzwerk</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight truncate">Lagerorte</h1>
        </div>
        <Button data-testid="new-location-btn" onClick={openNew}
          className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-sm uppercase tracking-wider font-semibold">
          <Plus size={16} className="mr-2" /> Neuer Lagerort
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(EMPTY); } }}>
        <DialogContent className="bg-zinc-950 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">
              {editingId ? "Lagerort bearbeiten" : "Neuer Lagerort"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Code *</Label>
              <Input data-testid="loc-field-code" value={form.code}
                disabled={!!editingId}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm font-mono w-full disabled:opacity-60" />
              {editingId && <div className="text-xs text-zinc-500 mt-1">Code kann nicht geändert werden (historische Referenzen).</div>}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Name *</Label>
              <Input data-testid="loc-field-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm w-full" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Typ</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="loc-field-type" className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  <SelectItem value="warehouse">Warenhaus</SelectItem>
                  <SelectItem value="shop">Shop</SelectItem>
                  <SelectItem value="vending_machine">Automat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Adresse</Label>
              <Input data-testid="loc-field-address" value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm w-full" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button data-testid="loc-save-btn" onClick={() => save.mutate()}
              disabled={!form.code || !form.name}
              className="bg-cyan-400 text-zinc-950 hover:bg-cyan-300 rounded-sm">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="locations-grid">
        {(locations.data || []).map((l) => (
          <div
            key={l.id}
            data-testid={`location-card-${l.id}`}
            onClick={() => nav(`/lagerbestand?location=${l.id}`)}
            className="group border border-zinc-800 bg-zinc-900 rounded-sm p-5 cursor-pointer transition-colors hover:border-cyan-400/60 hover:bg-zinc-900/70"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-cyan-400">{TYPE_LABEL[l.type] || l.type}</div>
                <div className="font-display text-2xl font-bold uppercase tracking-tight mt-1 truncate">{l.name}</div>
                <div className="text-xs font-mono text-zinc-500 mt-1">{l.code}</div>
              </div>
              <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                <Button size="icon" variant="ghost" data-testid={`loc-edit-${l.id}`} onClick={() => openEdit(l)}>
                  <Pencil size={14} />
                </Button>
                <Button size="icon" variant="ghost" className="text-rose-400"
                  data-testid={`loc-delete-${l.id}`} onClick={() => confirmDelete(l)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
            {l.address && <div className="text-sm text-zinc-400 mt-3 break-anywhere">{l.address}</div>}
            <div className="mt-4 flex items-center justify-between gap-2">
              <span className={`text-xs uppercase tracking-wider ${l.active ? "text-emerald-400" : "text-zinc-500"}`}>
                {l.active ? "Aktiv" : "Inaktiv"}
              </span>
              <span
                data-testid={`loc-open-inventory-${l.id}`}
                className="text-[11px] uppercase tracking-widest text-zinc-500 group-hover:text-cyan-400 inline-flex items-center gap-1"
              >
                Bestand <ArrowRight size={12} />
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
